import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BlogCard } from "./components/blog-card";
import { CategoryFilter } from "./components/category-filter";
import { CommonPagination } from "./components/CommonPagination";
import { BlogPostApi } from "./services/BlogPostApi";

const Blogs = () => {
  const blogPostApi = new BlogPostApi();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "";
  const categoryIdParam = searchParams.get("categoryId") || "";
  const pageParam = searchParams.get("page")
    ? parseInt(searchParams.get("page"))
    : 1;

  const [categorySlug, setCategorySlug] = useState(categoryParam);
  const [categoryId, setCategoryId] = useState(categoryIdParam);
  const [page, setPage] = useState(pageParam);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [meta, setMeta] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
    limit: 9,
  });

  // Reset page when category changes
  useEffect(() => {
    if (categorySlug !== categoryParam) {
      setPage(1);
    }
  }, [categorySlug, categoryParam]);

  // Update URL when category or page changes
  useEffect(() => {
    const params = new URLSearchParams();

    if (categorySlug !== "") {
      params.set("category", categorySlug);
    }

    if (categoryId) {
      params.set("categoryId", categoryId);
    }

    if (page > 1) {
      params.set("page", page.toString());
    }

    setSearchParams(params, { replace: true });
  }, [categorySlug, categoryId, page, setSearchParams]);

  // Fetch posts and set featured post
  useEffect(() => {
    setIsLoading(true);
    const fetchPosts = async () => {
      try {
        const response = await blogPostApi.getAll({
          categoryId: categoryId || undefined,
          pageNo: page, // Convert 1-based to 0-based for API
          pageSize: itemsPerPage,
          status: "Published",
        });

        // Handle PostListResponse structure: { posts: PaginatedResponse<PostResponse> }
        let validPosts = [];
        let pageNum = page;
        let total = 0;
        let totalPages = 1;

        // Check if response has posts property with PaginatedResponse structure
        if (response?.posts?.content && Array.isArray(response.posts.content)) {
          const paginatedPosts = response.posts;
          validPosts = paginatedPosts.content || [];
          pageNum = (paginatedPosts.number || 0) + 1; // API uses 0-based, convert to 1-based
          total = paginatedPosts.totalElements || 0;
          totalPages = paginatedPosts.totalPages || 1;
        }
        // Fallback: Check if response has posts array directly (legacy format)
        else if (Array.isArray(response?.posts)) {
          validPosts = response.posts;
          pageNum = response.page || page;
          total = response.total || validPosts.length;
          totalPages = response.totalPages || Math.ceil(total / itemsPerPage);
        }
        // Fallback: Check if response is paginated structure directly
        else if (response?.content && Array.isArray(response.content)) {
          validPosts = response.content;
          pageNum = (response.number || 0) + 1; // API uses 0-based, we use 1-based
          total = response.totalElements || 0;
          totalPages = response.totalPages || 1;
        }
        // Fallback: response might be the posts array directly
        else if (Array.isArray(response)) {
          validPosts = response;
          total = validPosts.length;
          totalPages = Math.ceil(total / itemsPerPage);
        }

        setPosts(validPosts);
        setFeaturedPost(validPosts[0] || null);
        setMeta({
          page: pageNum,
          total,
          totalPages,
          limit: itemsPerPage,
        });
      } catch (error) {
        console.error("Failed to fetch posts:", error);
        setPosts([]);
        setFeaturedPost(null);
        setMeta({
          page: 1,
          total: 0,
          totalPages: 1,
          limit: itemsPerPage,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug, categoryId, page, itemsPerPage]);

  const handleCategoryChange = (newCategory, newCategoryId) => {
    setCategoryId(newCategoryId);
    setCategorySlug(newCategory);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    // Scroll to top when changing pages
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1); // Reset to first page when changing items per page
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <main className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4 py-8 md:py-12 max-w-7xl">
          {/* Featured Post Section */}
          <section className="mb-14">
            {isLoading ? (
              <div className="h-64 flex items-center justify-center bg-white rounded-2xl border border-gray-200 shadow-sm animate-pulse">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              featuredPost && <BlogCard post={featuredPost} featured />
            )}
          </section>

          {/* Category Filter */}
          <section className="mb-10">
            <CategoryFilter
              activeCategory={categorySlug}
              activeCategoryId={categoryId}
              onCategoryChange={handleCategoryChange}
            />
          </section>

          {/* Blog Posts Grid */}
          <section>
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-64 bg-white rounded-2xl border border-gray-200 shadow-sm animate-pulse"
                  />
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="text-2xl font-semibold mb-3 text-gray-900">
                  No posts found
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  No blog posts matching your criteria were found. Try changing
                  the category.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {posts.map((post, index) => (
                  <div
                    key={post.id}
                    className="animate-stagger"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <BlogCard post={post} />
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!isLoading && (
              <div className="mt-12">
                <CommonPagination
                  currentPage={meta.page}
                  totalPages={meta.totalPages}
                  totalRecords={meta.total}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Blogs;
